import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/#home' },
  { label: 'Marketplace', href: '/#marketplace' },
  { label: 'For Homes', href: '/#homes' },
  { label: 'For Businesses', href: '/#businesses' },
  { label: 'For Partners', href: '/#partners' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Solar Calculator', href: '/#solar-calculator' },
  { label: 'Solar Energy', href: '/solar-energy-australia' },
  { label: 'Wind Energy', href: '/wind-energy-solutions' },
  { label: 'Hydrogen', href: '/hydrogen-energy-australia' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Ref always holds the latest pathname — avoids stale closure on first click
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const isHomePage = location.pathname === '/' || location.pathname === '';

  /**
   * Central navigation handler for hash links.
   * Using this via onPointerDown + onClick provides the fastest possible response.
   */
  const handleHashNav = useCallback((href: string) => {
    const [rawPath, rawHash] = href.split('#');
    const targetPath = rawPath || '/';
    const sectionId = rawHash;

    const currentPath = pathnameRef.current;
    const onHomePage = currentPath === '/' || currentPath === '';

    if (onHomePage && targetPath === '/') {
      // Same-page — scroll directly
      if (sectionId) {
        const el = document.getElementById(sectionId);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 112;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      }
      navigate({ pathname: '/', hash: sectionId ? `#${sectionId}` : '' }, { replace: true });
    } else {
      // Cross-page — navigate to home with hash
      navigate({ pathname: targetPath, hash: sectionId ? `#${sectionId}` : '' });
    }
    setMobileOpen(false);
  }, [navigate]);

  // Track visible section on home page via IntersectionObserver
  useEffect(() => {
    if (!isHomePage) {
      setActiveSection(null);
      return;
    }

    const sectionIds = navLinks
      .filter(l => l.href.startsWith('/#'))
      .map(l => l.href.replace('/#', ''));

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter(e => e.isIntersecting);
        if (visibleEntries.length > 0) {
          const best = visibleEntries.reduce((prev, curr) =>
            curr.intersectionRatio > prev.intersectionRatio ? curr : prev
          );
          setActiveSection(best.target.id);
        }
      },
      {
        rootMargin: '-8% 0px -55% 0px',
        threshold: [0, 0.2, 0.4, 0.6],
      }
    );

    const timeout = setTimeout(() => {
      sectionIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }, 300);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [isHomePage, location.hash]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isLinkActive = useCallback((href: string): boolean => {
    if (href.startsWith('/#')) {
      if (!isHomePage) return false;
      const sectionId = href.replace('/#', '');
      if (activeSection) return activeSection === sectionId;
      return sectionId === 'home';
    }
    if (href === '/blog') return location.pathname.startsWith('/blog');
    return location.pathname === href;
  }, [isHomePage, activeSection, location.pathname]);

  const activeLinkClass = 'text-primary-700 font-semibold bg-primary-100/80';
  const inactiveLinkClass = 'text-foreground-600 hover:text-foreground-950 hover:bg-primary-50/80';
  const baseLinkClass = 'whitespace-nowrap px-3 py-1.5 text-xs font-medium transition-colors duration-200 rounded-md cursor-pointer';

  const linkClass = (href: string) =>
    `${baseLinkClass} ${isLinkActive(href) ? activeLinkClass : inactiveLinkClass}`;

  const mobileLinkClass = (href: string) =>
    `block w-full text-left px-4 py-3.5 text-base font-medium rounded-xl transition-colors cursor-pointer ${
      isLinkActive(href)
        ? 'text-primary-700 font-semibold bg-primary-100/80'
        : 'text-foreground-700 hover:text-foreground-950 hover:bg-primary-50/80'
    }`;

  const renderNavLink = (link: typeof navLinks[number]) => {
    if (link.href.startsWith('/#')) {
      // Use <button> for hash links — fires on first click, no focus-before-click issue
      return (
        <button
          key={link.href}
          type="button"
          onClick={() => handleHashNav(link.href)}
          className={linkClass(link.href)}
        >
          {link.label}
        </button>
      );
    }
    return (
      <Link key={link.href} to={link.href} className={linkClass(link.href)}>
        {link.label}
      </Link>
    );
  };

  const handleEditBlogClick = () => {
    setPassword('');
    setPasswordError(false);
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = () => {
    if (password === 'AKEnergyBlog2026') {
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError(false);
      navigate('/blog/admin');
    } else {
      setPasswordError(true);
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePasswordSubmit();
    else if (e.key === 'Escape') handlePasswordCancel();
  };

  const navRow1 = navLinks.slice(0, 5);
  const navRow2 = navLinks.slice(5);

  return (
    <>
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'border-b border-background-200/60' : ''
      }`}
      style={scrolled ? {
        background: 'linear-gradient(135deg, oklch(0.95 0.04 148 / 0.92) 0%, oklch(0.96 0.04 96 / 0.92) 50%, oklch(0.94 0.04 245 / 0.92) 100%)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      } : undefined}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center h-[52px] md:h-[60px] pt-[5mm] pb-[5mm]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 no-underline shrink-0">
            <img
              src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
              alt="AK Energy logo"
              className="h-8 md:h-9 w-auto"
              loading="eager"
            />
          </Link>

          {/* Desktop: two-line right-aligned nav */}
          <div className="hidden md:flex flex-col items-end gap-0.5 ml-auto">
            <div className="flex items-center gap-0.5">
              {navRow1.map(renderNavLink)}
            </div>
            <div className="flex items-center gap-1">
              {navRow2.map(renderNavLink)}
              <span className="w-px h-4 bg-background-300 mx-1"></span>
              <button
                type="button"
                onClick={handleEditBlogClick}
                className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-foreground-800 transition-colors duration-200 rounded-md hover:bg-primary-50/80 cursor-pointer"
                title="Edit Blog"
              >
                <i className="ri-edit-line mr-1"></i>
                Edit Blog
              </button>
              {/* Get Started — button fires on first click, no focus-before-navigate */}
              <button
                type="button"
                onClick={() => handleHashNav('/#contact')}
                className="whitespace-nowrap px-4 py-1.5 text-xs font-semibold rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer ml-0.5"
              >
                Get Started
              </button>
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-primary-50/80 transition-colors cursor-pointer ml-auto"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[52px] z-40 animate-fade-in" style={{ background: 'linear-gradient(135deg, oklch(0.95 0.04 148 / 0.92) 0%, oklch(0.96 0.04 96 / 0.92) 50%, oklch(0.94 0.04 245 / 0.92) 100%)' }}>
          <div className="flex flex-col px-6 py-6 gap-1">
            {navLinks.map((link, i) =>
              link.href.startsWith('/#') ? (
                <button
                  key={link.href}
                  type="button"
                  onClick={() => handleHashNav(link.href)}
                  className={mobileLinkClass(link.href)}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={mobileLinkClass(link.href)}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="mt-4 pt-4 border-t border-white/30 space-y-2">
              <button
                type="button"
                onClick={() => { setMobileOpen(false); handleEditBlogClick(); }}
                className="block w-full text-left px-4 py-3.5 text-base font-medium text-foreground-600 hover:text-foreground-950 hover:bg-primary-50/80 rounded-xl transition-colors cursor-pointer"
              >
                <i className="ri-edit-line mr-2"></i>
                Edit Blog
              </button>
              <button
                type="button"
                onClick={() => handleHashNav('/#contact')}
                className="block w-full text-center px-6 py-3.5 text-base font-semibold rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-all cursor-pointer"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>

    {/* Password Modal */}
    {showPasswordModal && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handlePasswordCancel} />
        <div className={`relative bg-white rounded-2xl w-full max-w-sm p-6 shadow-lg animate-fade-in-up ${passwordError ? 'animate-shake' : ''}`}>
          <button
            type="button"
            onClick={handlePasswordCancel}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="text-center mb-5">
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
              <i className="ri-lock-line text-xl text-primary-500"></i>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">Admin Access</h3>
            <p className="text-sm text-foreground-500 mt-1">Enter the password to manage blog posts</p>
          </div>
          <div className="space-y-3">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(false); }}
                onKeyDown={handleKeyDown}
                placeholder="Enter password"
                autoFocus
                className={`w-full px-4 py-3 text-sm rounded-xl border-2 bg-background-50 text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors ${
                  passwordError
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-background-200 focus:border-primary-400'
                }`}
              />
              {passwordError && (
                <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                  <i className="ri-error-warning-line"></i>
                  Incorrect password. Please try again.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handlePasswordSubmit}
              disabled={!password.trim()}
              className="w-full px-5 py-3 text-sm font-semibold rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-400 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Unlock Editor
            </button>
          </div>
        </div>
      </div>
    )}

    <style>{`
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        20% { transform: translateX(-8px); }
        40% { transform: translateX(8px); }
        60% { transform: translateX(-6px); }
        80% { transform: translateX(6px); }
      }
      .animate-shake { animation: shake 0.4s ease-in-out; }
    `}</style>
    </>
  );
}